/**
 * Internationalization Module for KUINI Website
 * Handles Arabic/English language switching with RTL support
 * Uses i18next for translation management
 */

import i18next from 'i18next';
import Backend from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

// Supported languages configuration
const SUPPORTED_LANGUAGES = ['en', 'ar'];
const DEFAULT_LANGUAGE = 'en';
const RTL_LANGUAGES = ['ar'];

/**
 * Initialize i18next with configuration
 * Sets up backend loading, language detection, and fallback options
 */
export async function initI18n() {
    try {
        await i18next
            .use(Backend)
            .use(LanguageDetector)
            .init({
                lng: DEFAULT_LANGUAGE,
                fallbackLng: DEFAULT_LANGUAGE,
                supportedLngs: SUPPORTED_LANGUAGES,

                // Language detection configuration
                detection: {
                    order: ['localStorage', 'navigator', 'htmlTag'],
                    caches: ['localStorage'],
                    lookupLocalStorage: 'kuini-language'
                },

                // Backend configuration for loading translation files
                backend: {
                    loadPath: '/locales/{{lng}}/{{ns}}.json'
                },

                // Namespace configuration
                ns: ['translation'],
                defaultNS: 'translation',

                // Interpolation options
                interpolation: {
                    escapeValue: false // React already escapes values
                }
            });

        // Set initial language and direction
        const currentLang = i18next.language || DEFAULT_LANGUAGE;
        updateDocumentLanguage(currentLang);

        console.log('✅ i18next initialized successfully with language:', currentLang);
        return true;
    } catch (error) {
        console.error('❌ Failed to initialize i18next:', error);
        return false;
    }
}

/**
 * Change the current language
 * Updates document direction and stores preference
 * @param {string} language - Language code (en, ar)
 */
export async function changeLanguage(language) {
    if (!SUPPORTED_LANGUAGES.includes(language)) {
        console.warn(`Unsupported language: ${language}`);
        return false;
    }

    try {
        await i18next.changeLanguage(language);
        updateDocumentLanguage(language);

        // Store language preference
        localStorage.setItem('kuini-language', language);

        // Trigger custom event for components to update
        window.dispatchEvent(new CustomEvent('languageChanged', {
            detail: { language }
        }));

        console.log('✅ Language changed to:', language);
        return true;
    } catch (error) {
        console.error('❌ Failed to change language:', error);
        return false;
    }
}

/**
 * Update document attributes for language and direction
 * Handles RTL/LTR switching for Arabic/English
 * @param {string} language - Language code
 */
function updateDocumentLanguage(language) {
    const isRTL = RTL_LANGUAGES.includes(language);

    // Update document attributes
    document.documentElement.lang = language;
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';

    // Add/remove RTL class for styling
    document.documentElement.classList.toggle('rtl', isRTL);
    document.documentElement.classList.toggle('ltr', !isRTL);

    // Update body class for theme adjustments
    document.body.classList.toggle('rtl', isRTL);
    document.body.classList.toggle('ltr', !isRTL);
}

/**
 * Get translation for a key
 * @param {string} key - Translation key (e.g., 'nav.products')
 * @param {object} options - Interpolation options
 * @returns {string} Translated text
 */
export function t(key, options = {}) {
    return i18next.t(key, options);
}

/**
 * Get current language
 * @returns {string} Current language code
 */
export function getCurrentLanguage() {
    return i18next.language || DEFAULT_LANGUAGE;
}

/**
 * Check if current language is RTL
 * @returns {boolean} True if current language is RTL
 */
export function isRTL() {
    return RTL_LANGUAGES.includes(getCurrentLanguage());
}

/**
 * Get all supported languages
 * @returns {Array} Array of supported language codes
 */
export function getSupportedLanguages() {
    return [...SUPPORTED_LANGUAGES];
}

/**
 * Create language switcher HTML
 * @returns {string} HTML for language switcher
 */
export function createLanguageSwitcher() {
    const currentLang = getCurrentLanguage();
    const otherLang = currentLang === 'en' ? 'ar' : 'en';
    const otherLangText = currentLang === 'en' ? 'العربية' : 'English';

    return `
        <div class="dropdown dropdown-end">
            <div tabindex="0" role="button" class="btn btn-ghost btn-sm">
                <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"></path>
                </svg>
                ${otherLangText}
            </div>
            <ul tabindex="0" class="dropdown-content menu bg-base-100 rounded-box z-[1] w-32 p-2 shadow-lg border">
                <li>
                    <button onclick="switchLanguage('${otherLang}')" class="text-sm">
                        <span>${otherLangText}</span>
                    </button>
                </li>
            </ul>
        </div>
    `;
}

/**
 * Global function for language switching (called from HTML)
 * @param {string} language - Target language
 */
window.switchLanguage = function(language) {
    changeLanguage(language).then(() => {
        // Reload translations in the UI
        updateUITranslations();
    });
};

/**
 * Update UI with current translations
 * Finds elements with data-i18n attributes and updates their content
 */
export function updateUITranslations() {
    // Update elements with data-i18n attributes
    document.querySelectorAll('[data-i18n]').forEach(element => {
        const key = element.getAttribute('data-i18n');
        const translatedText = t(key);

        if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
            // Handle form inputs
            if (element.getAttribute('placeholder') !== null) {
                element.placeholder = translatedText;
            } else {
                element.value = translatedText;
            }
        } else {
            // Handle regular elements
            element.textContent = translatedText;
        }
    });

    // Update elements with data-i18n-html attributes (for HTML content)
    document.querySelectorAll('[data-i18n-html]').forEach(element => {
        const key = element.getAttribute('data-i18n-html');
        const translatedHTML = t(key);
        element.innerHTML = translatedHTML;
    });

    // Update language switcher
    const languageSwitcher = document.getElementById('language-switcher');
    if (languageSwitcher) {
        languageSwitcher.innerHTML = createLanguageSwitcher();
    }
}

// Export the i18next instance for direct access if needed
export { i18next };